import type React from "react";
import { Box, type InputProps as MuiInputProps, Typography } from "@mui/material";
import * as monaco from "monaco-editor";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

interface JsonEditorProps extends MuiInputProps {
  label: string;
  variant?: string;
  helperText?: string;
  height?: string | number;
}

const InnerWrap = styled.div<{ $borderColor: string }>`
  height: 100%;
  border: 1px solid ${props => props.$borderColor};
  border-radius: 0.5rem;
  overflow: hidden;
`;

const JsonEditor = (props: JsonEditorProps) => {
  const {
    value,
    defaultValue,
    disabled,
    readOnly,
    onChange,
    name,
    label,
    required,
    error,
    helperText,
    height = "200px",
    sx,
  } = props;

  const editorRef = useRef<HTMLDivElement>(null);
  const [editor, setEditor] = useState<monaco.editor.IStandaloneCodeEditor | null>(null);
  const isSettingValue = useRef(false);
  const borderColor = error ? "red" : "#ccc";

  useEffect(() => {
    if (editorRef.current && !editor) {
      const monacoEditor = monaco.editor.create(editorRef.current, {
        value: (value || defaultValue) as string || "",
        language: "json",
        theme: "vs",
        minimap: { enabled: false },
        lineNumbers: "on",
        readOnly: disabled || readOnly,
        scrollBeyondLastLine: false,
        automaticLayout: true,
        formatOnPaste: true,
        formatOnType: true,
        scrollbar: {
          alwaysConsumeMouseWheel: false,
        },
      });

      monacoEditor.onDidChangeModelContent(() => {
        // Don't trigger onChange if we're programmatically setting the value
        if (isSettingValue.current) {
          return;
        }

        const currentValue = monacoEditor.getValue();
        if (onChange) {
          const event = {
            target: {
              value: currentValue,
              name: name,
            },
            currentTarget: {
              value: currentValue,
              name: name,
            },
          } as React.ChangeEvent<HTMLTextAreaElement>;
          onChange(event);
        }
      });

      setEditor(monacoEditor);
    }

    return () => {
      if (editor) {
        editor.dispose();
      }
    };
  }, []);

  // Update editor value when prop changes
  useEffect(() => {
    if (editor && typeof value === "string" && value !== editor.getValue()) {
      isSettingValue.current = true;
      editor.setValue(value as string || "");
      // Reset the flag after a brief delay to allow the change event to be processed
      setTimeout(() => {
        isSettingValue.current = false;
      }, 10);
    }
  }, [value, editor]);

  // Update editor options when props change
  useEffect(() => {
    if (editor) {
      editor.updateOptions({
        readOnly: disabled || readOnly,
      });
    }
  }, [disabled, readOnly, editor]);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        //gap: '0.5rem',
        ...sx,
      }}
    >
      <Typography
        component={"label"}
        variant={"h6"}
        //htmlFor="monaco-editor"
      >
        {label}{required && " *"}
      </Typography>
      <InnerWrap $borderColor={borderColor}>
        <div
          ref={editorRef}
          style={{ height: typeof height === 'string' ? height : `${height}px`, width: '100%' }}
        />
      </InnerWrap>
      {helperText && (
        <Box sx={{fontSize: "0.75rem", color: "#666", marginTop: 0}}>
          {helperText}
        </Box>
      )}
    </Box>
  );
};

export default JsonEditor;